/**
 * ユーザーデータのファサード（EP-1）。
 *
 * 起動時に会員セッション（{@link Session}）を 1 度だけ問い合わせ、
 *   - 未ログイン → localStorage
 *   - ログイン中 → サーバ `/me/*`（初回だけ localStorage の内容をマージ送信）
 * のどちらかに接続する。呼び出し側（ビューワ・UI）はこの違いを見ない。
 */
import { LocalUserData, safeStorage } from './local-store';
import { markMerged, planMerge } from './merge';
import { RemoteUserData } from './remote-store';
import { Session } from './session';
import type { ListType, MarkType, UserDataStore, UserItem } from './store';
import { nowSec } from './store';

export interface UserDataOptions {
  /** API の基準 URL（manifest の source から導く。例 `https://host/api/v1`）。 */
  base: string;
  /** ゲスト時に localStorage を使うか（既定 true）。 */
  local?: boolean;
  view?: Window | null;
  /** 会員セッション（公開コメントと共有して問い合わせを 1 度にする）。 */
  session?: Session;
}

export class UserData implements UserDataStore {
  private readonly local: LocalUserData;
  private readonly remote: RemoteUserData;
  private readonly session: Session;
  private active: UserDataStore;
  private ready: Promise<void> | null = null;
  private signedIn = false;

  constructor(options: UserDataOptions) {
    this.local = new LocalUserData(
      options.local === false ? null : safeStorage(options.view ?? null),
    );
    this.remote = new RemoteUserData(options.base);
    this.session = options.session ?? new Session(options.base);
    this.active = this.local;
  }

  /** ログイン状態を解決し、必要ならマージまで終える（複数回呼んでも 1 度だけ走る）。 */
  connect(): Promise<void> {
    return (this.ready ??= this.resolve());
  }

  /** ログイン中か（connect() 後に確定）。 */
  isSignedIn(): boolean {
    return this.signedIn;
  }

  async list(type: ListType): Promise<UserItem[]> {
    await this.connect();
    return this.active.list(type);
  }

  async put(type: MarkType, item: UserItem): Promise<void> {
    await this.connect();
    return this.active.put(type, item);
  }

  async remove(type: MarkType, book: string, page?: number): Promise<void> {
    await this.connect();
    return this.active.remove(type, book, page);
  }

  async progress(book: string): Promise<number | null> {
    await this.connect();
    return this.active.progress(book);
  }

  async saveProgress(book: string, page: number, updatedAt?: number): Promise<void> {
    await this.connect();
    return this.active.saveProgress(book, page, updatedAt);
  }

  private async resolve(): Promise<void> {
    this.signedIn = (await this.session.user()) !== null;
    if (!this.signedIn) return;

    this.active = this.remote;
    await this.mergeOnce();
  }

  /** 初回ログイン時だけ、ゲストの内容をサーバへ引き継ぐ。 */
  private async mergeOnce(): Promise<void> {
    const plan = planMerge(this.local.current());
    if (!plan) return;

    for (const group of plan.marks) {
      await this.remote.putBulk(group.type, group.items);
    }
    await this.remote.putProgress(plan.progress);
    // 送信後に印を付ける（tombstone を持たない代わりに、マージは 1 度きり）
    this.local.replace(markMerged(this.local.current(), nowSec()));
  }
}

/**
 * manifest の URL（`…/api/v1/books/{slug}/manifest`）から API の基準 URL を導く。
 * 標準形でなければ null（ユーザーデータ機能を無効にする）。
 */
export function apiBaseFrom(source: string): string | null {
  const marker = '/books/';
  const index = source.indexOf(marker);
  if (index < 0) return null;
  return source.slice(0, index);
}
